import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload as UploadIcon, FileText, CheckCircle, XCircle, Loader, AlertTriangle } from 'lucide-react';
import { uploadApi } from '../lib/api';
import toast from 'react-hot-toast';

interface UploadedFile {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  documentId?: string;
  error?: string;
  progress?: number;
  isDuplicate?: boolean;
  duplicateOf?: any[];
}

export default function Upload() {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const queryClient = useQueryClient();

  const uploadMutation = useMutation({
    mutationFn: (files: File[]) => uploadApi.upload(files),
    onSuccess: (response) => {
      const uploadedDocuments = response.data.documents;
      
      // Count duplicates
      const duplicateCount = uploadedDocuments.filter((d: any) => d.isDuplicate).length;
      
      setFiles((prevFiles) =>
        prevFiles.map((f) => {
          const uploaded = uploadedDocuments.find(
            (d: any) => d.filename === f.file.name
          );
          if (uploaded) {
            return {
              ...f,
              status: uploaded.success ? 'success' : 'error',
              documentId: uploaded.documentId,
              error: uploaded.error,
              isDuplicate: uploaded.isDuplicate,
              duplicateOf: uploaded.duplicateOf,
            };
          }
          return f;
        })
      );

      // Show different messages based on duplicates
      if (duplicateCount > 0) {
        toast.success(`Uploaded ${response.data.uploaded} file(s) - ${duplicateCount} duplicate(s) detected`, {
          icon: '⚠️',
          duration: 5000,
        });
      } else {
        toast.success(`Successfully uploaded ${response.data.uploaded} file(s)`);
      }
      
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Upload failed');
      setFiles((prevFiles) =>
        prevFiles.map((f) => ({
          ...f,
          status: 'error',
          error: error.message,
        }))
      );
    },
  });

  const onDrop = (acceptedFiles: File[]) => {
    const newFiles: UploadedFile[] = acceptedFiles.map((file) => ({
      file,
      status: 'pending',
    }));

    setFiles((prev) => [...prev, ...newFiles]);

    // Start upload
    uploadMutation.mutate(acceptedFiles);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.tiff', '.webp'],
    },
    maxSize: 50 * 1024 * 1024, // 50MB
  });

  const clearCompleted = () => {
    setFiles((prev) =>
      prev.filter((f) => f.status !== 'success' && f.status !== 'error')
    );
  };

  const clearAll = () => {
    setFiles([]);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Upload Documents</h1>
        <p className="text-gray-600">
          Upload receipts, invoices, and other documents for AI processing
        </p>
      </div>

      {/* Upload Area */}
      <div
        {...getRootProps()}
        className={`card border-2 border-dashed cursor-pointer transition-colors ${
          isDragActive
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 hover:border-primary-400'
        }`}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center justify-center py-12">
          <UploadIcon
            className={`h-16 w-16 mb-4 ${
              isDragActive ? 'text-primary-600' : 'text-gray-400'
            }`}
          />
          <p className="text-lg font-medium text-gray-900 mb-2">
            {isDragActive ? 'Drop files here' : 'Drop files here or click to browse'}
          </p>
          <p className="text-sm text-gray-500">
            Supports PDF, PNG, JPG, JPEG, TIFF, WEBP (max 50MB)
          </p>
        </div>
      </div>

      {/* Upload Queue */}
      {files.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Upload Queue ({files.length})
            </h2>
            <div className="flex gap-2">
              <button onClick={clearCompleted} className="btn-ghost text-sm">
                Clear Completed
              </button>
              <button onClick={clearAll} className="btn-ghost text-sm">
                Clear All
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {files.map((uploadedFile, index) => (
              <div
                key={index}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
              >
                <FileText className="h-8 w-8 text-gray-400 flex-shrink-0" />
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {uploadedFile.file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(uploadedFile.file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>

                {uploadedFile.status === 'pending' && (
                  <div className="flex items-center gap-2 text-gray-500">
                    <span className="text-sm">Pending</span>
                  </div>
                )}

                {uploadedFile.status === 'uploading' && (
                  <div className="flex items-center gap-2 text-primary-600">
                    <Loader className="h-5 w-5 animate-spin" />
                    <span className="text-sm">Uploading...</span>
                  </div>
                )}

                {uploadedFile.status === 'success' && (
                  <div className="flex items-center gap-2">
                    {uploadedFile.isDuplicate ? (
                      <>
                        <AlertTriangle className="h-5 w-5 text-yellow-600" />
                        <span className="text-sm text-yellow-700 font-medium">
                          Duplicate Detected
                        </span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <span className="text-sm text-green-600">Success</span>
                      </>
                    )}
                  </div>
                )}

                {uploadedFile.status === 'error' && (
                  <div className="flex items-center gap-2 text-red-600">
                    <XCircle className="h-5 w-5" />
                    <span className="text-sm">
                      {uploadedFile.error || 'Failed'}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {uploadMutation.isPending && (
            <div className="mt-4 p-4 bg-primary-50 rounded-lg">
              <p className="text-sm text-primary-700">
                Uploading files... AI processing will begin automatically.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Tips */}
      <div className="card bg-blue-50 border-blue-200">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">Tips</h3>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Upload multiple files at once for batch processing</li>
          <li>• AI will automatically extract text, categorize, and detect receipts</li>
          <li>• Processing typically takes 5-30 seconds per document</li>
          <li>• Check the Documents page to view processed files</li>
        </ul>
      </div>
    </div>
  );
}

